/**
 * 账户 / 分类名称的模糊搜索匹配（原文 + 拼音全拼 + 拼音首字母）。
 *
 * 依据：**B 域 PRD §5.8**（原文**逐字照抄自 §5.8.2 的六级权重表**）·
 * `AC-B7.1`~`AC-B7.12` · §5.8.4 噪音规避 · §5.8.5 与 C3 交易搜索的边界（防双实现）。
 *
 * ## 🔴 为什么放在 `client/lib` 而不是共享层
 *
 * §5.8.6 双端口径：**Web 支持 / 小程序不做**（负责人 2026-09-26 拍板）。
 * 放共享层会诱导小程序也接一份 ⇒ 与拍板相悖；而这里的能力是**纯前端内存匹配**，
 * 服务端一行都不用（§5.8.5：候选集在内存、无网络往返）。⇒ 归属 `client`。
 *
 * ## 🔴 为什么不重写（PRD §5.8 正文原话）
 *
 * > v1 资产可移植：`client/src/lib/pinyinMatch.ts`（含单测）+ `components/SearchSelect.tsx`
 * > **已实现且质量高 → 直接移植，不重写。**
 *
 * 移植自 v1（`D:\project\WorkBuddy\家盈\family-finance\packages\client\src\lib\pinyinMatch.ts`），
 * 保留了它踩过的坑（见下「不重写」段）。
 *
 * ## 🔴 v1 的实战教训（移植时**必须保留**）
 *
 * - **不用 `pinyin-pro` 的 `match()`**：实测它对「中国银行etc信用卡」搜 `zs` 会因模糊音（s/x）
 *   返回命中下标 `[0,7]`，与「招商银行」的 `[0,1]` 起点相同 → 排序无法区分优先级，且会产生
 *   噪音候选。本模块改用 `pinyin()` 自建索引串做 `indexOf`，结果确定、排序权重可控。
 * - 三个索引串都走 `indexOf`（而非 `startsWith`），因此天然支持「中间匹配」。
 * - 索引按名称缓存：候选集只有几十到上百个短词，且账户名 / 分类名在会话内基本不变，
 *   因此「转拼音」每个名称只做一次（§5.8.3「性能」行的口径）。
 *
 * 命中优先级（rank 越小越靠前）—— 逐条对应 §5.8.2 的档位表：
 *   1 原文前缀 → 2 原文子串 → 3 首字母前缀 → 4 首字母子串 → 5 全拼前缀 → 6 全拼子串
 *
 * 样例（PRD §5.8.2 表格里的同一组）：
 *   「zs」      → 招商银行 / 浙商银行（首字母前缀）
 *   「etc」     → 中国银行etc信用卡（原文子串，中间匹配）
 *   「zhaoshang」→ 招商银行（全拼前缀）
 *   「yinhang」 → 招商银行 / 银行（全拼子串）
 */

import { pinyin } from 'pinyin-pro';

/** 一个名称的三个匹配索引串。 */
export interface NameIndex {
  /** 原文小写（保留中文与英文/数字），用于原文子串匹配 */
  lower: string;
  /** 全拼串（无声调、无分隔），如「招商银行」→ `zhaoshangyinhang` */
  full: string;
  /** 首字母串，如「招商银行」→ `zsyh` */
  first: string;
}

/**
 * 命中档位 —— **数值越小优先级越高**。🔴 六级，逐条对应 B 域 PRD §5.8.2。
 *
 * ⚠️ 改这里的数值等于改搜索排序口径 ⇒ 必须同步 §5.8.2 与 `AC-B7.*`。
 */
export const MATCH_RANK = {
  /** 档 1 · 原文前缀命中（最强） */
  NAME_PREFIX: 1,
  /** 档 2 · 原文子串命中（如 etc） */
  NAME_SUBSTR: 2,
  /** 档 3 · 拼音首字母前缀命中（如 zs） */
  FIRST_PREFIX: 3,
  /** 档 4 · 拼音首字母子串命中 */
  FIRST_SUBSTR: 4,
  /** 档 5 · 全拼前缀命中 */
  FULL_PREFIX: 5,
  /** 档 6 · 全拼子串命中（最弱） */
  FULL_SUBSTR: 6,
} as const;

/** 一次命中的档位与起始位置（**同档内按位置靠前的优先** —— §5.8.2 正文）。 */
export interface MatchHit {
  rank: number;
  pos: number;
}

const indexCache = new Map<string, NameIndex>();

/** 缓存上限，防止极端情况下（反复改名）无限增长。 */
const INDEX_CACHE_LIMIT = 2000;

/** `pinyin()` 在 `type: 'array'` 下返回数组；此处做一次兜底以兼容类型定义差异。 */
function toSegments(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [String(value)];
}

/**
 * 建立（并缓存）一个名称的匹配索引。
 *
 * 非中文内容（英文、数字、符号）会被 pinyin-pro **原样保留**，因此「etc」这类英文片段
 * 既能参与原文匹配，也会出现在全拼串与首字母串中 ⇒ §5.8.2 末行
 * 「英文 / 数字原样保留，同时进入原文串、全拼串与首字母串（故「etc」三处皆可命中）」。
 */
export function buildNameIndex(name: string): NameIndex {
  const cached = indexCache.get(name);
  if (cached !== undefined) return cached;

  const full = toSegments(pinyin(name, { toneType: 'none', type: 'array' }))
    .join('')
    .toLowerCase();
  const first = toSegments(
    pinyin(name, { pattern: 'first', toneType: 'none', type: 'array' }),
  )
    .join('')
    .toLowerCase();

  const index: NameIndex = { lower: name.toLowerCase(), full, first };

  if (indexCache.size >= INDEX_CACHE_LIMIT) indexCache.clear();
  indexCache.set(name, index);
  return index;
}

/** 清空索引缓存（测试隔离时可用）。 */
export function clearNameIndexCache(): void {
  indexCache.clear();
}

/**
 * 归一化查询串 —— §5.8.2「归一化（查询串）」行：
 * `trim` → 转小写 → **去除内部空白**（「z s」≡「zs」）。
 */
export function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * 判断一个名称是否命中查询串。
 *
 * @param name 候选名称（原文）
 * @param rawQuery 用户输入（可为空；**空串视为全部命中** —— §5.8.3「空输入」行）
 * @returns 命中信息（档位 + 起始位置）；未命中返回 `null`
 */
export function matchName(name: string, rawQuery: string): MatchHit | null {
  const q = normalizeQuery(rawQuery);
  if (q === '') return { rank: MATCH_RANK.NAME_PREFIX, pos: 0 };

  const { lower, first, full } = buildNameIndex(name);

  const namePos = lower.indexOf(q);
  if (namePos === 0) return { rank: MATCH_RANK.NAME_PREFIX, pos: 0 };
  if (namePos > 0) return { rank: MATCH_RANK.NAME_SUBSTR, pos: namePos };

  const firstPos = first.indexOf(q);
  if (firstPos === 0) return { rank: MATCH_RANK.FIRST_PREFIX, pos: 0 };
  if (firstPos > 0) return { rank: MATCH_RANK.FIRST_SUBSTR, pos: firstPos };

  const fullPos = full.indexOf(q);
  if (fullPos === 0) return { rank: MATCH_RANK.FULL_PREFIX, pos: 0 };
  if (fullPos > 0) return { rank: MATCH_RANK.FULL_SUBSTR, pos: fullPos };

  return null;
}

/** 命中比较器：**先比档位，再比起始位置**（§5.8.2 排列口径）。 */
export function compareHits(a: MatchHit, b: MatchHit): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  return a.pos - b.pos;
}

/**
 * 按名称模糊搜索并排序（纯函数）。
 *
 * 同档同位置时保持传入顺序（**稳定排序**）—— §5.8.3「排序稳定性」行：
 * 调用方可以先把候选集按业务顺序排好（例如分类按树序），搜索结果里同分项会延续该顺序。
 *
 * @param items 候选集合
 * @param getName 从候选项取名称（对应 §5.8.4 的 `matchText`，**不传则退化为 `label`**）
 * @param rawQuery 用户输入；**空串时原样返回**（§5.8.3：不重排）
 */
export function searchByName<T>(
  items: readonly T[],
  getName: (item: T) => string,
  rawQuery: string,
): T[] {
  const q = normalizeQuery(rawQuery);
  if (q === '') return [...items];

  const scored: { item: T; hit: MatchHit; order: number }[] = [];
  items.forEach((item, order) => {
    const hit = matchName(getName(item), q);
    if (hit !== null) scored.push({ item, hit, order });
  });

  scored.sort((a, b) => compareHits(a.hit, b.hit) || a.order - b.order);
  return scored.map((s) => s.item);
}
